import { useEffect, useRef, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { getRequest, postRequest } from "../pages/Common/api/apiService";
import Swal from "sweetalert2";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api/activity";
const IDLE_THRESHOLD_MS = 60000; // 60 seconds minimum

async function post(endpoint, body) {
  try {
    await fetch(`${API_BASE}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch (err) {
    console.warn(`[IdleTracker] ${endpoint} failed:`, err.message);
  }
}

export function useIdleTracker({ userId } = {}) {
  const location = useLocation();

  const sessionIdRef     = useRef(null);
  const detectorRef      = useRef(null);
  const heartbeatRef     = useRef(null);
  const prevPathRef      = useRef(null);
  const prevEnteredAtRef = useRef(null);
  const currentStateRef  = useRef("active");

  const [permissionState, setPermissionState] = useState("prompt");
  const [userState,       setUserState]       = useState("active");
  const [screenState,     setScreenState]     = useState("unlocked");
  const [isSupported,     setIsSupported]     = useState(true);
  const [showBanner,      setShowBanner]      = useState(false);

  const checkWFHTracker = localStorage.getItem('WFHStartIdleTracker');
  

  // ── Handle IdleDetector state change ─────────────────────────────
  const handleIdleChange = useCallback((detector) => {
    const checkWFHCurrentStatus = localStorage.getItem('WFHCurrentStatus');
    console.log(checkWFHCurrentStatus,'checkWFHCurrentStatus');
    if (checkWFHCurrentStatus !== "working") {
      console.log("[IdleTracker] Ignored idle event because WFH status:", checkWFHCurrentStatus);
      return;
    }
    const newUserState   = detector.userState;
    const newScreenState = detector.screenState;
    const prevState      = currentStateRef.current;

    if (newUserState === prevState) {
      console.log("[IdleTracker] Duplicate state ignored:", newUserState);
      return;
    }
    setUserState(newUserState);
    setScreenState(newScreenState);
    currentStateRef.current = newUserState;
    const now = new Date().toISOString();
    console.log(`[IdleTracker] Current State → userState: ${newUserState}, screenState: ${newScreenState}, TIME: ${new Date().toLocaleString()}, prevstate: ${prevState}`);

    // active → idle
    if (newUserState === "idle" && prevState === "active") {
      console.log("[IdleTracker] IDLE detected — posting idle/start", {
        idleStart: now,
        screenState: newScreenState,
      });
      postRequest("/idletracker/start", {
        idleStart: now,
        screenState: newScreenState,
        prevState: prevState
      });
    }

    // idle → active
    if (newUserState === "active" && prevState === "idle") {
      console.log("[IdleTracker] ACTIVE resumed — posting idle/end", {
        idleEnd: now
      });
      postRequest("/idletracker/end", {idleEnd: now});
    }
  }, []);

  // ── Start IdleDetector (called after permission granted) ─────────
  const startDetector = useCallback(async () => {
    if (!("IdleDetector" in window)) {
      setIsSupported(false);
      return;
    }
    if (detectorRef.current) return; // already running

    try {
      console.log("[IdleTracker] Starting IdleDetector...");
      const detector = new window.IdleDetector();
      detector.addEventListener("change", () => handleIdleChange(detector));
      await detector.start({ threshold: IDLE_THRESHOLD_MS });
      detectorRef.current = detector;
      setShowBanner(false);
      localStorage.setItem('showBanner',false);
      console.log("[IdleTracker] IdleDetector started — OS-level tracking active");
      if(localStorage.getItem('showPermissionGranded') === 'true'){
        await Swal.fire({
          icon: "success",
          title: "Permission Granted",
          text: "Permission granted. Click the Login button to start your WFH session.",
          timer: 3000,
          timerProgressBar: true,
          showConfirmButton: false
        });
        localStorage.setItem('showPermissionGranded','false');
      }
    } catch (err) {
      console.warn("[IdleTracker] detector.start() failed:", err.message);
    }
  }, [handleIdleChange]);

  //  requestPermission must be called on user click 
  // This is called when user clicks the "Allow Tracking" button in the banner
  const requestPermission = useCallback(async () => {
    console.log("[IdleTracker] Requesting idle-detection permission...");
    if (!("IdleDetector" in window)) return;    
    try {
      console.log("[IdleTracker] Requesting idle-detection permission...");
      // This MUST be called from a user gesture (button click)
      const result = await window.IdleDetector.requestPermission();
      console.log("[IdleTracker] Permission result:", result);
      setPermissionState(result);

      if (result === "granted") {
        console.log("[IdleTracker] Permission granted — starting IdleDetector");
        await startDetector();
        console.log("[IdleTracker] IdleDetector started after permission granted");
      }
      return result;
    } catch (err) {
      console.warn("[IdleTracker] Permission request failed:", err.message);
    }
  }, [startDetector]);

  useEffect(() => {
    if (!userId ) return;

    let mounted = true;
    (async () => {
      // Check browser support
      if (!("IdleDetector" in window)) {
        setIsSupported(false);
        console.warn("[IdleTracker] IdleDetector not supported — use Chrome 94+");
        return;
      }

      // FIX 2: Check existing permission state first
      // If already granted from a previous visit → start detector immediately
      // If prompt → show banner so user can click Allow (user gesture required)
      // If denied → show banner with instructions
      try {
        const status = await navigator.permissions.query({ name: "idle-detection" });
        console.log("[IdleTracker] Current permission state:", status.state);
        setPermissionState(status.state);

        if (status.state === "granted") {
          // Already granted before — start detector right away, no prompt needed
          if (mounted) await startDetector();
          setShowBanner(false);
          localStorage.setItem('showBanner',false);
        } else {
          // 'prompt' or 'denied' — show banner so user can click Allow button
          // Cannot call requestPermission() here — needs user gesture (click)
          setShowBanner(true);
          localStorage.setItem('showBanner',true);
          console.log("[IdleTracker] Showing permission banner — waiting for user click");
        }

        // Listen for permission changes (e.g., user grants via browser settings)
        status.addEventListener("change", () => {
          setPermissionState(status.state);
          if (status.state === "granted" && mounted) {
            startDetector();
            setShowBanner(false);
            localStorage.setItem('showBanner',false);
          }
        });
      } catch (err) {
        console.warn("[IdleTracker] Permission query failed:", err.message);
        // Fallback: show banner anyway
        setShowBanner(true);
      }

      prevPathRef.current      = location.pathname;
      prevEnteredAtRef.current = new Date().toISOString();
    })();

    return () => {
      mounted = false;
      detectorRef.current = null;
      clearInterval(heartbeatRef.current);
    };
  }, [userId, checkWFHTracker]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track route changes ───────────────────────────────────────────
  useEffect(() => {
    if (!sessionIdRef.current) return;
    const prev      = prevPathRef.current;
    const enteredAt = prevEnteredAtRef.current;
    if (prev && prev !== location.pathname) {
      post("/page-visit", {
        sessionId: sessionIdRef.current,
        path:      location.pathname,
        title:     document.title,
        enteredAt: new Date().toISOString(),
        prevPath:  prev,
        leftAt:    enteredAt,
      });
    }
    prevPathRef.current      = location.pathname;
    prevEnteredAtRef.current = new Date().toISOString();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  

  return {
    userState,
    screenState,
    isIdle:           userState === "idle",
    isLocked:         screenState === "locked",
    permissionState,
    isSupported,
    showBanner,       // ← use this to show Allow button in your UI
    requestPermission,
    startDetector,
  };
}